import { LineChart } from '@mui/x-charts/LineChart';
import { useXScale, useYScale } from '@mui/x-charts/hooks';

export interface DashboardLineSeries {
  label: string;
  data: number[];
  color?: string;
  area?: boolean;
}

interface DashboardLineChartProps {
  categories: string[];
  series: DashboardLineSeries[];
  height?: number;
  valueFormatter?: (value: number | null) => string;
  /** Default (unset) keeps the library's default top placement. 'bottom' moves the legend below
   * the plot and reserves margin for it — used by the TV display, where charts sit in narrower
   * columns and a top/inline legend can overlap the plot area. */
  legendPosition?: 'bottom';
  /** Shows each point's value just above it. @mui/x-charts has no built-in point-label feature
   * (unlike BarChart's `barLabel`), so this renders a small custom SVG text layer as the
   * LineChart's `children` — it reads the same x/y scales the chart itself uses (via useXScale/
   * useYScale), so the labels always line up with the actual rendered points. */
  showValueLabels?: boolean;
}

function LineValueLabels({
  categories,
  series,
  formatValue,
}: {
  categories: string[];
  series: DashboardLineSeries[];
  formatValue: (value: number) => string;
}) {
  const xScale = useXScale<'point'>();
  const yScale = useYScale<'linear'>();

  return (
    <g>
      {series.map((s, seriesIndex) =>
        s.data.map((value, i) => {
          if (value === null || value === undefined) return null;
          const x = xScale(categories[i]);
          const y = yScale(value);
          if (typeof x !== 'number' || typeof y !== 'number') return null;
          return (
            <text
              key={`${seriesIndex}-${i}`}
              x={x}
              y={y - 8}
              textAnchor="middle"
              fontSize={10}
              fontWeight={700}
              fill={s.color ?? '#333'}
            >
              {formatValue(value)}
            </text>
          );
        }),
      )}
    </g>
  );
}

export default function DashboardLineChart({ categories, series, height = 300, valueFormatter, legendPosition, showValueLabels }: DashboardLineChartProps) {
  // A caller explicitly asking for a bottom legend wants it shown even for a single series (it
  // reads as a caption naming what the one line is) — the 2+-series default only applies when
  // legendPosition is left unset.
  const showsLegend = series.length >= 2 || legendPosition === 'bottom';
  const bottomMargin = legendPosition === 'bottom' && showsLegend ? 55 : 30;
  const formatValue = (value: number) => valueFormatter?.(value) ?? value.toLocaleString();

  return (
    <LineChart
      height={height}
      xAxis={[{ scaleType: 'point', data: categories, tickLabelStyle: { fontSize: 11 } }]}
      series={series.map((s) => ({
        label: s.label,
        data: s.data,
        color: s.color,
        area: s.area,
        curve: 'monotoneX',
        valueFormatter,
      }))}
      margin={{ top: showValueLabels ? 24 : 10, right: 10, bottom: bottomMargin, left: 50 }}
      slotProps={{
        legend: {
          hidden: !showsLegend,
          direction: 'row',
          // Omit `position` entirely rather than setting it to `undefined` when not overriding —
          // an explicit `undefined` key still shadows @mui/x-charts' own internal default during
          // its slotProps merge, crashing LegendPerItem (it reads `position.horizontal`
          // unconditionally, with no null-guard for a missing position).
          ...(legendPosition === 'bottom' ? { position: { vertical: 'bottom' as const, horizontal: 'middle' as const } } : {}),
        },
      }}
      grid={{ horizontal: true }}
    >
      {showValueLabels && <LineValueLabels categories={categories} series={series} formatValue={formatValue} />}
    </LineChart>
  );
}
